// Constants
export const DASHBOARD_TYPE = 1;
export const REPORT_TYPE = 2;
export const STATUS_DRAFT = 0;
export const STATUS_PUBLISHED = 1;

// Re-export admin utilities from common utils for convenience
// These can also be imported directly from '@common/utils/permissionsUtils'
export { isUserAdmin, createAdminOnlyAction, createAdminOnlyCreateButton } from '@common/utils/permissionsUtils';

export const getStatusMap = (t) => ({
  [STATUS_DRAFT]: t('reports.status.draft'),
  [STATUS_PUBLISHED]: t('reports.status.published'),
});

export const getTypeMap = (t) => ({
  [DASHBOARD_TYPE]: t('reports.types.dashboard'),
  [REPORT_TYPE]: t('reports.types.report'),
});

/**
 * Filter type definitions for reports
 * isAsync: true - options loaded from API, false - static options
 */
export const REPORT_FILTER_TYPES = [
  { value: 'name', isAsync: true },
  { value: 'description', isAsync: true },
  { value: 'status', isAsync: false },
  { value: 'creator', isAsync: true },
  { value: 'updatedAt', isAsync: true },
];

export const getFilterTypes = (t) => REPORT_FILTER_TYPES.map((type) => ({
  ...type,
  label: t(`reports.filters.${type.value}`),
}));

/**
 * Check if a filter type is async (loads options from API)
 */
export const isAsyncFilterType = (filterType) => {
  const type = REPORT_FILTER_TYPES.find((t) => t.value === filterType);
  return type?.isAsync ?? false;
};

export const getFilterValues = (t) => ({
  type: [
    { value: DASHBOARD_TYPE, label: t('reports.types.dashboard') },
    { value: REPORT_TYPE, label: t('reports.types.report') },
  ],
  status: [
    { value: STATUS_DRAFT, label: t('reports.status.draft') },
    { value: STATUS_PUBLISHED, label: t('reports.status.published') },
  ],
});

/**
 * Check if a filter is valid (has type and non-empty value)
 * @param {Object} filter - Filter object
 * @returns {boolean} - true if filter is valid
 */
export const isValidFilter = (filter) => 
  filter?.type && 
  filter.value !== '' && 
  filter.value !== null && 
  filter.value !== undefined;

/**
 * Build filters for report search API
 * - Filters out invalid/empty filters
 * - Ensures type filter is set to REPORT_TYPE (2)
 * - Handles status filter based on admin privileges:
 *   - Non-admin: Always adds status=STATUS_PUBLISHED (1)
 *   - Admin: Only adds status filter if user explicitly selected one
 * 
 * @param {Array} filters - Array of filter objects from UI
 * @param {boolean} isAdmin - Whether user has admin privileges
 * @param {boolean} enableDebugLogging - Whether to log debug info (default: false)
 * @returns {Array} - Array of filters ready to send to API
 */
export const buildReportFilters = (filters = [], isAdmin = false, enableDebugLogging = false) => {
  // Filter out invalid/empty filters
  const validFilters = filters.filter(isValidFilter);
  
  // Ensure type filter is set to REPORT_TYPE
  const hasTypeFilter = validFilters.some(f => f.type === 'type');
  let reportFilters = hasTypeFilter 
    ? validFilters.map(f => f.type === 'type' ? { type: 'type', value: REPORT_TYPE } : f)
    : [{ type: 'type', value: REPORT_TYPE }, ...validFilters];

  // Handle status filter based on admin status
  // IMPORTANT: Remove ALL status filters first, then add back only if needed
  reportFilters = reportFilters.filter(f => f.type !== 'status');
  
  if (!isAdmin) {
    // For non-admin users, always filter for published reports
    reportFilters.push({ type: 'status', value: STATUS_PUBLISHED });
  } else {
    // For admin users, only add status filter if they explicitly selected one
    const userSelectedStatusFilter = validFilters.find(f => f.type === 'status' && isValidFilter(f));
    if (userSelectedStatusFilter) {
      reportFilters.push({ type: 'status', value: userSelectedStatusFilter.value });
    }
    // If no status filter selected, don't add one - this allows admin to see all statuses
  }

  // Debug logging (only in development)
  if (enableDebugLogging && process.env.NODE_ENV === 'development') {
    // Removed console.log - use console.error for debugging if needed
  }

  return reportFilters;
};
